import { flow, pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';
import { array, nonEmptyArray, option, set, string } from 'fp-ts';
import {
    getStringProp,
    getStringArrayProp,
    getProp,
    primitiveTypeUniq,
    isEmptyRecord,
    compactArray,
    getItemByCommonPropertyValue,
} from '@mews/fp';
import {
    composeProps,
    createAsyncMetaSelectors,
    createPropsExtender,
    createScopedSelector,
    Selector,
} from '@mews/framework';
import { Reservation, ServiceOrderOrigin } from '@mews-commander/data-access';
import { RouteName } from '@mews-commander/enums';
import { ApplicationState, isCanceledState } from '@mews-commander/types';
import { ResolveRoute } from '@mews/routing';
import { getTimeClass } from '@mews-commander/utils';
import { getBookableServices, getServicesByIds } from '@mews-commander/legacy-entities/src/ducks/services';
import {
    getCurrentEnterpriseReservationsByIds,
    getCurrentEnterpriseReservationsServiceIds,
} from '@mews-commander/legacy-entities/src/selectors/reservations';
import { META_STATE_SUCCEEDED } from '@mews-commander/legacy-entities/src/constants';
import { isBookableService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { moduleSelector } from '../../module';
import { reservationModuleStateKey } from '../../reducers';
import { INITIAL_STATE } from '../../reducers/reservationModuleState';
import { ReservationTab } from '../../../../types/reservationTab';

type ToggleObject = Record<string, true>;
type GetToggleObject = Selector<ApplicationState, { scope: string }, ToggleObject>;
interface ScopedData {
    checkedReservationIds: Set<string>;
    showAllServiceOrderIds: ToggleObject;
    openedReservationIds: ToggleObject;
    noteIdsToBeDeleted: string[];
    selectedReservationServiceId: string | null;
    canceledReservationsDataLoaded: boolean;
    orderItems: typeof META_STATE_SUCCEEDED;
    tab: ReservationTab;
}

const reservationModuleStateParentSelector = createSelector(
    moduleSelector,
    (module) => module[reservationModuleStateKey]
);
const reservationModuleStateSelector: Selector<ApplicationState, { scope: string }, ScopedData> = createScopedSelector({
    parentSelector: reservationModuleStateParentSelector,
    initialState: INITIAL_STATE,
});
export const getCheckedReservationsSet = createSelector(
    reservationModuleStateSelector,
    ({ checkedReservationIds }) => checkedReservationIds
);
export const getCheckedReservationsCount = flow(getCheckedReservationsSet, set.size);
export const getCheckedReservationIds = createSelector(getCheckedReservationsSet, set.toArray(string.Ord));

export const getFirstCheckedReservationId = flow(getCheckedReservationIds, (reservationIds) =>
    reservationIds.length > 0 ? reservationIds[0] : undefined
);

export const isSingleReservationChecked = flow(getCheckedReservationsCount, (count) => count === 1);
export const isReservationChecked = createSelector(
    getStringProp('reservationId'),
    getCheckedReservationsSet,
    (reservationId, checkedReservationsSet) => checkedReservationsSet.has(reservationId)
);
export const isAnyReservationByIdsChecked = createSelector(
    getStringArrayProp('reservationIds'),
    getCheckedReservationsSet,
    (reservationIds, checkedReservations) =>
        pipe(
            reservationIds,
            array.some((reservationId) => checkedReservations.has(reservationId))
        )
);

export const areAllReservationByIdsChecked = createSelector(
    getStringArrayProp('reservationIds'),
    getCheckedReservationsSet,
    (reservationIds, checkedReservations) =>
        pipe(
            reservationIds,
            array.every((reservationId) => checkedReservations.has(reservationId))
        )
);
const getOpenedReservationIdsObject: GetToggleObject = createSelector(
    reservationModuleStateSelector,
    (state) => state.openedReservationIds
);

export const areAllReservationsOpened = createSelector(
    getStringArrayProp('reservationIds'),
    getOpenedReservationIdsObject,
    (reservationIds, checkedReservations) =>
        pipe(
            reservationIds,
            array.every((reservationId) => checkedReservations[reservationId])
        )
);
export const getOpenedReservationsIds = createSelector(getOpenedReservationIdsObject, Object.keys);
export const isReservationOpened = createSelector(
    getStringProp('reservationId'),
    getOpenedReservationIdsObject,
    (reservationId, openedReservations) => Boolean(openedReservations[reservationId])
);
const getReservationShowAllNotesObject: GetToggleObject = createSelector(
    reservationModuleStateSelector,
    (state) => state.showAllServiceOrderIds
);
export const areCanceledReservationDataLoaded = flow(
    reservationModuleStateSelector,
    (state) => state.canceledReservationsDataLoaded
);
export const showAllServiceOrderNotes = createSelector(
    getStringProp('serviceOrderId'),
    getReservationShowAllNotesObject,
    (serviceOrderId, showAllNotesObject) => Boolean(showAllNotesObject[serviceOrderId])
);

export const getCheckedReservations = composeProps(
    getCurrentEnterpriseReservationsByIds,
    createPropsExtender('ids', getCheckedReservationIds)
);

export const getCheckedReservationsActionLogUrl = createSelector(
    getProp<ResolveRoute>()('resolveRoute'),
    getCheckedReservationIds,
    (resolveRoute, reservationIds) => resolveRoute(RouteName.ReservationEventLog, { query: { Ids: reservationIds } })
);

export const getFirstCheckedReservation = createSelector(getCheckedReservations, (reservations) =>
    reservations.length > 0 ? reservations[0] : undefined
);

export const getServiceCheckedReservations = createSelector(
    getCheckedReservations,
    getStringProp('serviceId'),
    (checkedReservations, serviceId) => checkedReservations.filter((r) => r.serviceId === serviceId)
);

export const getServiceCheckedReservationsIds = createSelector(
    getServiceCheckedReservations,
    array.map((r) => r.id)
);

export const getNonCancelledCheckedReservations = createSelector(
    getCheckedReservations,
    array.filter((r) => !isCanceledState(r.serviceOrderState))
);

export const getCancelledCheckedReservations = createSelector(
    getCheckedReservations,
    array.filter((r: Reservation) => isCanceledState(r.serviceOrderState))
);

export const getCancelledCheckedReservationsCount = createSelector(getCancelledCheckedReservations, array.size);

export const getServiceNonCancelledCheckedReservations = createSelector(
    getServiceCheckedReservations,
    array.filter((r) => !isCanceledState(r.serviceOrderState))
);

export const getNonCancelledCheckedReservationIds = createSelector(
    getNonCancelledCheckedReservations,
    array.map((r) => r.id)
);

export const getNoteIdsToBeDeleted = createSelector(
    reservationModuleStateSelector,
    (state) => state.noteIdsToBeDeleted
);

export const getCheckedReservationsServiceIds = createSelector(
    getCheckedReservations,
    flow(
        array.map((r) => r.serviceId),
        primitiveTypeUniq
    )
);

export const hasSelectionPreauthorizationEnabled = flow(
    composeProps(getServicesByIds, createPropsExtender('ids', getCheckedReservationsServiceIds)),
    array.some((service) =>
        isBookableService(service) ? !!service.data.value?.visitOptions.requirePreauthorizationForStart : false
    )
);

export const getCheckedReservationsAvailabilityBlockIds = createSelector(
    getCheckedReservations,
    flow(
        array.filterMap(({ availabilityBlockId }) => option.fromNullable(availabilityBlockId)),
        primitiveTypeUniq
    )
);

export const isAnyReservationChecked = createSelector(getCheckedReservationsCount, (count) => count > 0);

export const getFirstCheckedReservationServiceId = flow(getCheckedReservationsServiceIds, (serviceIds) =>
    serviceIds.length > 0 ? serviceIds[0] : undefined
);

export const getFirstCheckedReservationAvailabilityBlockId = flow(
    getCheckedReservationsAvailabilityBlockIds,
    (availabilityBlockIds) => (availabilityBlockIds.length > 0 ? availabilityBlockIds[0] : undefined)
);

export const getSelectedServices = createSelector(
    getBookableServices,
    getCheckedReservationsServiceIds,
    (services, serviceIds) =>
        // @ts-expect-error No index signature with a parameter of type 'string' was found on type '{} | EnforceKeyableIdRecord<Identifiable>'.
        !isEmptyRecord(services) ? compactArray(serviceIds.map((serviceId) => services[serviceId])) : []
);

export const getReservationsServices = createSelector(
    getBookableServices,
    getCurrentEnterpriseReservationsServiceIds,
    (services, serviceIds) =>
        // @ts-expect-error No index signature with a parameter of type 'string' was found on type '{} | EnforceKeyableIdRecord<Identifiable>'.
        !isEmptyRecord(services) ? compactArray(serviceIds.map((serviceId) => services[serviceId])) : []
);

export const getSelectedTimeGroupedServices = createSelector(
    getSelectedServices,
    nonEmptyArray.groupBy((service) => getTimeClass(service) ?? '')
);

export const areCheckedReservationsFromMultipleServices = createSelector(
    getCheckedReservationsServiceIds,
    (serviceIds) => serviceIds.length > 1
);

export const areCheckedReservationsFromSameService = createSelector(
    isAnyReservationChecked,
    areCheckedReservationsFromMultipleServices,
    (anyReservationChecked, fromMultipleServices) => anyReservationChecked && !fromMultipleServices
);

export const getSelectedReservationServiceId = createSelector(
    reservationModuleStateSelector,
    (state) => state.selectedReservationServiceId
);

export const getSelectedTab = createSelector(reservationModuleStateSelector, (state) => state.tab);
export const isGroupTabSelected = flow(getSelectedTab, (tab) => tab === ReservationTab.Group);

const getOrderItemsMeta = createSelector(reservationModuleStateSelector, (scopedData) => scopedData.orderItems.meta);
export const [areOrderItemsFetching, hasOrderItemsFetchingFailed] = createAsyncMetaSelectors(getOrderItemsMeta);
export const getCheckedReservationsHaveCommanderServiceOrderOrigin = flow(
    getCheckedReservations,
    getItemByCommonPropertyValue('serviceOrderOrigin'),
    (serviceOrderOrigin) => serviceOrderOrigin === ServiceOrderOrigin.Commander
);
